import { createSlice } from "@reduxjs/toolkit";

import {
  initialVoiceListPaginationParams,
  VOICE_VISIBILITY,
  VOICES_SORT_KEYS,
  LOCALSTORAGE_ITEMS,
  VOICE_NO_ACCENT,
  ACCENT_SAMPLES_TYPES,
  AWS_BUCKET_URL,
  RESPEECHER_TTS_LANGUAGE_CODES,
  NARRATION_STYLE_BASIC,
} from "../constants";
import { clearStore } from "actions/initialization";
import {
  voicesListThunk,
  privateVoicesCheckThunk,
  setFavoriteVoiceThunk,
  updateVoiceSettingsThunk,
  searchVoicesThunk,
  voicesInitialSearchListThunk,
  voicesByIdsThunk,
  accentInfosThunk,
  demoSetsThunk,
  getNarrationStyleTagsThunk,
  getNarrationStyleTagGroupsThunk,
} from "thunks/voice";
import localStorageService from "services/localStorage";
import { sortByObjectKey } from "utils";

export const getInitialFilters = () => ({
  type:
    localStorageService.getItem(LOCALSTORAGE_ITEMS.filters.voices.type) || [],
  age: localStorageService.getItem(LOCALSTORAGE_ITEMS.filters.voices.age) || [],
  gender:
    localStorageService.getItem(LOCALSTORAGE_ITEMS.filters.voices.gender) || [],
  pitch:
    localStorageService.getItem(LOCALSTORAGE_ITEMS.filters.voices.pitch) || [],
  category:
    localStorageService.getItem(LOCALSTORAGE_ITEMS.filters.voices.category) ||
    [],
  narrationStyle:
    localStorageService.getItem(
      LOCALSTORAGE_ITEMS.filters.voices.narrationStyle
    ) || [],
  nationality:
    localStorageService.getItem(
      LOCALSTORAGE_ITEMS.filters.voices.nationality
    ) || [],
});

export const initialState = {
  ...initialVoiceListPaginationParams,
  sort: {
    key: VOICES_SORT_KEYS.DEFAULT.key,
    direction: VOICES_SORT_KEYS.DEFAULT.direction,
  },
  filters: getInitialFilters(),
  ids: [],
  hash: {},
  loading: {
    list: false,
    privateCheck: false,
    fetch: false,
    favorite: false,
    update: false,
    search: false,
    byIds: false,
    accentInfos: false,
    demoSets: false,
    narrationStyleTagGroupsList: false,
    narrationStyleTagsList: false,
  },
  error: {
    list: null,
    privateCheck: null,
    fetch: null,
    favorite: null,
    update: null,
    search: null,
    byIds: null,
    accentInfos: null,
    demoSets: null,
    narrationStyleTagGroupsList: null,
    narrationStyleTagsList: null,
  },
  selectedId: undefined,
  selectedForCoversion: {},
  search: {
    query: undefined,
    ids: [],
    hash: {},
  },
  hasPrivateVoices: false,
  accentInfos: { ids: [], hash: {} },
  demoSets: { ids: [], hash: {} },
  narrationStyleTags: { ids: [], hash: {} },
  narrationStyleTagGroups: { ids: [], hash: {} },
  lastUsed: [],
};

export const getDefaultAccentOrNarrationStyle = (items) => {
  const favorite = items.find((item) => item?.settings?.favorite);
  const defaultItem = items.find((item) => item.is_default);
  return items.length > 0
    ? favorite
      ? favorite
      : defaultItem
      ? defaultItem
      : items[0]
    : undefined;
};

export const getNarrationStylesForSelectedLang = (
  narrationStyles,
  selectedLang
) => {
  // in cases where selectedLang is null but the voice has narration styles
  // we return narrationStyles and not [NARRATION_STYLE_BASIC]
  const isSupportedTTSLang =
    (!selectedLang ||
      RESPEECHER_TTS_LANGUAGE_CODES.includes(selectedLang.toLowerCase())) &&
    narrationStyles &&
    Array.isArray(narrationStyles) &&
    narrationStyles.length > 0;

  return isSupportedTTSLang ? narrationStyles : [NARRATION_STYLE_BASIC];
};

export const getSampleForAccentOrNarrationStyle = (accent, narrationStyle) => {
  const src = accent
    ? `${AWS_BUCKET_URL}/samples/${
        accent.id
      }/${ACCENT_SAMPLES_TYPES.casual.toLowerCase()}/0.wav`
    : narrationStyle
    ? `${AWS_BUCKET_URL}/samples/${narrationStyle.id}/default/0.wav`
    : undefined;

  const sample = {
    type: ACCENT_SAMPLES_TYPES.casual,
    src: src,
  };

  return sample;
};

export const voiceDTO = (voice) => {
  const accents = sortByObjectKey(
    (voice.accents || []).map((item) => ({ ...item, name: item?.info?.name })),
    "name_asc"
  ).sort((a, b) => (a.is_default === b.is_default ? 0 : a.is_default ? -1 : 1));
  const narrationStyles = sortByObjectKey(
    (voice.narration_styles || []).map((item) => ({
      ...item,
      name: item?.info?.name,
    })),
    "name_asc"
  ).sort((a, b) => (a.is_default === b.is_default ? 0 : a.is_default ? -1 : 1));
  return {
    id: voice.id,
    owner_id: voice.owner_id,
    name: voice.name,
    visibility: voice.visibility,
    species: voice.species,
    gender: voice.gender,
    pitch: voice.pitch,
    age_group: voice.age_group,
    pitch_group: voice.pitch_group,
    active: voice.active,
    created_at: voice.created_at,
    favorite: voice.favorite,
    available: voice.available,
    accents,
    image: voice.image_url,
    thumbnail: voice.thumbnail_url,
    defaultAccent: getDefaultAccentOrNarrationStyle(voice.accents),
    tiers: voice.tiers,
    nationality: voice.nationality,
    narrationStyles,
    voice_talent: voice.voice_talent,
    cost_multiplier: voice.cost_multiplier,
    info_url: voice.info_url,
    contact_url: voice.contact_url,
    category: voice.category,
    bg_image: voice.bg_image_url,
  };
};

const voiceSlice = createSlice({
  name: "voice",
  initialState,
  reducers: {
    voicesListSetPaginationData(state, action) {
      const { pagination, sort, filters, voiceTalent } = action.payload;
      if (pagination) {
        const { count, currentPage, limit } = pagination;
        if (currentPage) {
          state.pagination.currentPage = currentPage;
          state.pagination.offset = (currentPage - 1) * state.pagination.limit;
        }
        if (count) {
          state.pagination.count = count;
        }
        if (limit) {
          state.pagination.limit = limit;
        }
      }
      if (sort) {
        state.sort = sort;
      }
      if (filters) {
        state.filters = filters;
      }
      state.pagination.forceFetch = true;
    },
    setSelectedVoice(state, action) {
      const voiceId = action.payload;
      state.selectedId = voiceId;
    },
    addToConversionVoices(state, action) {
      const voices = action.payload;
      voices.forEach((data) => {
        state.selectedForCoversion[data.id] = data;
      });
    },
    removeFromConversionVoices(state, action) {
      const id = action.payload;
      if (state.selectedForCoversion[id]) {
        delete state.selectedForCoversion[id];
      }
    },
    clearConversionVoices(state) {
      state.selectedForCoversion = {};
    },
    setSearchResults(state, action) {
      const item = action.payload;
      state.ids = [item.id];
      state.hash[item.id] = item;
      state.pagination = initialVoiceListPaginationParams.pagination;
    },
    addToLastUsed(state, action) {
      const data = action.payload;
      state.lastUsed.push(data);
    },
    clearLastUsed(state) {
      state.lastUsed = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // #region pending
      .addCase(voicesListThunk.pending, (state) => {
        state.loading.list = true;
        state.ids = [];
        state.hash = {};
      })
      .addCase(privateVoicesCheckThunk.pending, (state) => {
        state.loading.privateCheck = true;
      })
      .addCase(setFavoriteVoiceThunk.pending, (state) => {
        state.loading.favorite = true;
      })
      .addCase(updateVoiceSettingsThunk.pending, (state) => {
        state.loading.update = true;
      })
      .addCase(searchVoicesThunk.pending, (state) => {
        state.loading.search = true;
      })
      .addCase(voicesInitialSearchListThunk.pending, (state) => {
        state.loading.search = true;
      })
      .addCase(voicesByIdsThunk.pending, (state) => {
        state.loading.byIds = true;
      })
      .addCase(accentInfosThunk.pending, (state) => {
        state.loading.accentInfos = true;
      })
      .addCase(demoSetsThunk.pending, (state) => {
        state.loading.demoSets = true;
      })
      .addCase(getNarrationStyleTagsThunk.pending, (state) => {
        state.loading.narrationStyleTagsList = true;
      })
      .addCase(getNarrationStyleTagGroupsThunk.pending, (state) => {
        state.loading.narrationStyleTagGroupsList = true;
      })
      // #endregion
      // #region fulfilled
      .addCase(voicesListThunk.fulfilled, (state, action) => {
        state.loading.list = false;
        const {
          meta: {
            arg: { privateVoicesCheck },
          },
          payload: { list, pagination },
        } = action;
        if (privateVoicesCheck) {
          state.hasPrivateVoices = Boolean(
            list.find((item) => item.visibility === VOICE_VISIBILITY.private)
          );
        } else {
          const ids = [];
          list.forEach((item) => {
            ids.push(item.id);
            state.hash[item.id] = voiceDTO(item);
          });
          state.ids = ids;
          const { limit, ...otherPaginationData } = pagination;
          state.pagination = {
            ...state.pagination,
            ...otherPaginationData,
            totalPages: Math.ceil(otherPaginationData.count / limit) || 1,
            forceFetch: false,
          };
        }
      })
      .addCase(privateVoicesCheckThunk.fulfilled, (state, action) => {
        state.loading.privateCheck = false;
        const {
          payload: { list },
        } = action;
        state.hasPrivateVoices = Boolean(
          list.find((item) => item.visibility === VOICE_VISIBILITY.private)
        );
      })
      .addCase(setFavoriteVoiceThunk.fulfilled, (state, action) => {
        state.loading.favorite = false;
        const {
          meta: {
            arg: { voiceId },
          },
          payload,
        } = action;
        state.hash[voiceId].favorite = payload.favorite;
      })
      .addCase(updateVoiceSettingsThunk.fulfilled, (state, action) => {
        state.loading.update = false;
        const {
          payload: { id: voice_id, accents, narration_styles },
        } = action;
        if (voice_id) {
          state.hash[voice_id].accents = accents;
          state.hash[voice_id].narrationStyles = narration_styles;
          state.hash[voice_id].defaultAccent = getDefaultAccentOrNarrationStyle(
            state.hash[voice_id].accents
          );
        }
      })
      .addCase(searchVoicesThunk.fulfilled, (state, action) => {
        state.loading.search = false;
        state.search.ids = [];
        state.search.hash = {};
        const items = action.payload;
        items.forEach((item) => {
          state.search.ids.push(item.id);
          state.search.hash[item.id] = voiceDTO(item);
        });
      })
      .addCase(voicesInitialSearchListThunk.fulfilled, (state, action) => {
        state.loading.search = false;
        const { list } = action.payload;
        const ids = [];
        list.forEach((item) => {
          ids.push(item.id);
          state.search.hash[item.id] = voiceDTO(item);
        });
        state.search.ids = ids;
      })
      .addCase(voicesByIdsThunk.fulfilled, (state, action) => {
        state.loading.byIds = false;
        const items = action.payload;
        items.forEach((item) => {
          state.hash[item.id] = voiceDTO(item);
        });
        // TODO obsolete?
        const lastUsedVoices =
          localStorageService.getItem(LOCALSTORAGE_ITEMS.lastUsedVoices) || {};
        if (Object.keys(lastUsedVoices).length > 0) {
          items
            .filter((item) => item.accents && item.accents.length > 0)
            .forEach((item) => {
              if (lastUsedVoices[item.id]) {
                const accent = item.accents.find(
                  (a) => a.id === lastUsedVoices[item.id].accent
                );
                if (accent) {
                  state.selectedForCoversion[item.id] = lastUsedVoices[item.id];
                } else {
                  // add no accent or first
                  const noAccent = item.accents.find(
                    (a) => a.locale !== VOICE_NO_ACCENT.key
                  );
                  if (noAccent) {
                    state.selectedForCoversion[item.id] = {
                      ...lastUsedVoices[item.id],
                      accent: noAccent.id,
                      pitch: 0,
                    };
                  } else {
                    state.selectedForCoversion[item.id] = {
                      ...lastUsedVoices[item.id],
                      accent: item.accents[0].id,
                      pitch: 0,
                    };
                  }
                }
              }
            });
        }
      })
      .addCase(accentInfosThunk.fulfilled, (state, action) => {
        state.loading.accentInfos = false;
        const items = action.payload;
        const ids = [];
        items.forEach((item) => {
          const key = item.locale;
          ids.push(key);
          state.accentInfos.hash[key] = item;
        });
        state.accentInfos.ids = ids;
      })
      .addCase(demoSetsThunk.fulfilled, (state, action) => {
        state.loading.demoSets = false;
        const items = action.payload;
        const ids = [];
        const voiceIds = [];
        items.forEach((item) => {
          const key = item.language;
          ids.push(key);
          state.demoSets.hash[key] = item;
          item.accents.forEach((accent) => {
            const { voice, ...accentData } = accent;
            voiceIds.push(accent.voice.id);
            state.hash[accent.voice.id] = voiceDTO({
              ...voice,
              accents: [accentData],
            });
          });
        });
        state.demoSets.ids = ids;
        state.ids = voiceIds;
      })
      .addCase(getNarrationStyleTagsThunk.fulfilled, (state, action) => {
        state.loading.narrationStyleTagsList = false;
        const items = action.payload;
        const ids = [];
        items.forEach((item) => {
          ids.push(item.id);
          state.narrationStyleTags.hash[item.id] = item;
        });
        state.narrationStyleTags.ids = ids;
      })
      .addCase(getNarrationStyleTagGroupsThunk.fulfilled, (state, action) => {
        state.loading.narrationStyleTagGroupsList = false;
        const items = action.payload;
        const ids = [];
        items.forEach((item) => {
          ids.push(item.id);
          state.narrationStyleTagGroups.hash[item.id] = item;
        });
        state.narrationStyleTagGroups.ids = ids;
      })
      // #endregion
      // #region rejected
      .addCase(voicesListThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.list = false;
        state.error.list = error;
      })
      .addCase(privateVoicesCheckThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.privateCheck = false;
        state.error.privateCheck = error;
      })
      .addCase(setFavoriteVoiceThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.favorite = false;
        state.error.favorite = error;
      })
      .addCase(updateVoiceSettingsThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.update = false;
        state.error.update = error;
      })
      .addCase(searchVoicesThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.search = false;
        state.error.search = error;
      })
      .addCase(voicesInitialSearchListThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.search = false;
        state.error.search = error;
      })
      .addCase(voicesByIdsThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.byIds = false;
        state.error.byIds = error;
      })
      .addCase(accentInfosThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.accentInfos = false;
        state.error.accentInfos = error;
      })
      .addCase(getNarrationStyleTagsThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.narrationStyleTagsList = false;
        state.error.narrationStyleTagsList = error;
      })
      .addCase(getNarrationStyleTagGroupsThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.narrationStyleTagGroupsList = false;
        state.error.narrationStyleTagGroupsList = error;
      })
      .addCase(demoSetsThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.demoSets = false;
        state.error.demoSets = error;
      })
      // #endregion
      .addCase(clearStore, () => initialState);
  },
});

export const actions = voiceSlice.actions;

export default voiceSlice.reducer;
